import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { types } from 'mobx-state-tree';
import { api } from 'src/api';
import { createAsync, createSilentAsync } from 'src/utils/asyncModel';

/** Guids come back from different services and may differ in case, so every map is keyed in one. */
const keyOf = id => String(id).toLowerCase();

/** A bill's (property, utility) pair, keyed the same way. */
const pairKey = (propertyId, utilityId) => "".concat(keyOf(propertyId), "|").concat(keyOf(utilityId));

/** As much of the property read as a link to the property needs. */

/**
 * The company's AppFolio chart of accounts. Read whole because it is what turns an account id into
 * something a person can read, and the ids on a bill's properties could be any of them.
 */
const listGlAccountsAsync = createAsync(() => {
  return async function listGlAccounts(options) {
    const resp = await api.Credentials.getGLAccounts(options);
    if (Array.isArray(resp === null || resp === void 0 ? void 0 : resp.response)) this.setGlAccounts(resp.response);
    return resp;
  };
});
const listPropertiesAsync = createAsync(() => {
  return async function listProperties(options) {
    const resp = await api.AppfolioProperties.listProperties(options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response)) this.setProperties(resp.response);
    return resp;
  };
});

/**
 * What one push attempt posted to. One attempt at a time is right for this action: the details view
 * shows one bill, so a read for a newly opened bill should cancel the one still out for the last.
 */
const readPushGlAccountsAsync = createAsync(pushAttemptId => {
  return async function readPushGlAccounts(options) {
    const resp = await api.AppfolioBills.getPushGlAccounts({
      pushAttemptId
    }, options);

    // A 404 is a real answer here — the attempt is not this company's, predates the stored body, or is
    // pending and not stored yet — and it is held as nothing recorded, which is what each of those is.
    if (resp !== null && resp !== void 0 && resp.isSuccessful) {
      var _resp$response;
      this.setRecordedAccounts(pushAttemptId, (_resp$response = resp.response) !== null && _resp$response !== void 0 ? _resp$response : {
        pushAttemptId,
        hasRecordedAccounts: false,
        lines: []
      });
    }
    return resp;
  };
});

/**
 * Which property utility each of a bill's (property, utility) pairs is (LB-5980). Silent: a caller without
 * `ManageProperty` is refused, and the bill details then fall back to each property's default account
 * rather than raise a toast over a bill the person can still read.
 */
const readPropertyUtilitiesAsync = createSilentAsync(pairs => {
  return async function readPropertyUtilities(options) {
    const resp = await api.Allocations.getApPushContext({
      items: pairs
    }, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response)) this.setPropertyUtilities(pairs, resp.response);
    return resp;
  };
});

/**
 * Which GL account each property's payables post to, and what that account is called.
 *
 * Two reads that are only useful together: the property says which account, the chart of accounts says
 * what it is. Kept out of the wider credentials store so the pages that read these for their own
 * purposes cannot cancel a read this one is waiting on.
 */
const AppfolioGlAccountsStore = types.model('AppfolioGlAccountsStore', {
  glAccountsByExternalId: types.optional(types.frozen(), {}),
  propertiesByLivableId: types.optional(types.frozen(), {}),
  /** Whether each read has answered, so "not configured" can be told from "not read yet". */
  hasReadGlAccounts: types.optional(types.boolean, false),
  hasReadProperties: types.optional(types.boolean, false),
  /** What each push attempt posted to, once read. Keyed by attempt, because that is what it describes. */
  recordedByAttemptId: types.optional(types.frozen(), {}),
  /** The property utility each pair asked about is: its id, or null when the property has none. */
  propertyUtilityByPair: types.optional(types.frozen(), {}),
  /** The portfolio each property asked about is in, or null when it is in none. */
  portfolioIdByPropertyId: types.optional(types.frozen(), {}),
  listGlAccountsAsync,
  listPropertiesAsync,
  readPropertyUtilitiesAsync,
  readPushGlAccountsAsync
}).volatile(() => ({
  portfolioReads: new Set()
})).actions(store => ({
  setRecordedAccounts(pushAttemptId, recorded) {
    store.recordedByAttemptId = _objectSpread(_objectSpread({}, store.recordedByAttemptId), {}, {
      [keyOf(pushAttemptId)]: recorded
    });
  },
  setPropertyUtilities(pairs, items) {
    const answered = _objectSpread({}, store.propertyUtilityByPair);

    // Every pair asked about has an answer, and one property-service did not return is a pair with no
    // property utility, which is also how the push reads it.
    pairs.forEach(pair => {
      answered[pairKey(pair.propertyId, pair.utilityId)] = null;
    });
    items.forEach(item => {
      if (item !== null && item !== void 0 && item.propertyId && item !== null && item !== void 0 && item.utilityId) {
        var _item$propertyUtility;
        answered[pairKey(item.propertyId, item.utilityId)] = (_item$propertyUtility = item.propertyUtilityId) !== null && _item$propertyUtility !== void 0 ? _item$propertyUtility : null;
      }
    });
    store.propertyUtilityByPair = answered;
  },
  setPropertyPortfolioId(propertyId, portfolioId) {
    store.portfolioIdByPropertyId = _objectSpread(_objectSpread({}, store.portfolioIdByPropertyId), {}, {
      [keyOf(propertyId)]: portfolioId
    });
  },
  setGlAccounts(accounts) {
    store.glAccountsByExternalId = Object.fromEntries(accounts.filter(account => account === null || account === void 0 ? void 0 : account.externalId).map(account => [keyOf(account.externalId), account]));
    store.hasReadGlAccounts = true;
  },
  setProperties(properties) {
    store.propertiesByLivableId = Object.fromEntries(properties.filter(property => property === null || property === void 0 ? void 0 : property.propertyLivableId).map(property => [keyOf(property.propertyLivableId), property]));
    store.hasReadProperties = true;
  }
})).actions(store => ({
  /**
   * Which portfolio a property is in, for a link to it on the property dashboard (LB-5981): a new tab
   * opened under the `all` portfolio is sent back to All properties, so the link needs the property's
   * own. Read once however many links ask, and quietly: a link that cannot be built is left out.
   */
  async readPropertyPortfolio(propertyId) {
    const key = keyOf(propertyId);
    if (key in store.portfolioIdByPropertyId || store.portfolioReads.has(key)) return;
    store.portfolioReads.add(key);
    try {
      const resp = await api.Properties.getProperty({
        propertyId
      }, {
        cancelToken: {}
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        var _ref, _resp$response$portfo, _resp$response2, _resp$response3;
        store.setPropertyPortfolioId(propertyId, (_ref = (_resp$response$portfo = (_resp$response2 = resp.response) === null || _resp$response2 === void 0 || (_resp$response2 = _resp$response2.portfolioIds) === null || _resp$response2 === void 0 ? void 0 : _resp$response2[0]) !== null && _resp$response$portfo !== void 0 ? _resp$response$portfo : (_resp$response3 = resp.response) === null || _resp$response3 === void 0 ? void 0 : _resp$response3.portfolioId) !== null && _ref !== void 0 ? _ref : null);
      }
    } catch (_unused) {
      // Left unread, so the next link to ask tries again.
    } finally {
      store.portfolioReads.delete(key);
    }
  }
})).views(store => ({
  /** The portfolio a property is in: its id, null when it is in none, undefined until read. */
  getPropertyPortfolioId(propertyId) {
    return propertyId ? store.portfolioIdByPropertyId[keyOf(propertyId)] : undefined;
  },
  /**
   * The account a payable for this property posts to, or null when the property has none configured,
   * is not mapped to AppFolio, or has not been read yet — `hasRead` is what separates those.
   */
  getApGlAccount(propertyLivableId) {
    var _store$propertiesByLi, _store$glAccountsByEx;
    if (!propertyLivableId) return null;
    const externalId = (_store$propertiesByLi = store.propertiesByLivableId[keyOf(propertyLivableId)]) === null || _store$propertiesByLi === void 0 ? void 0 : _store$propertiesByLi.accountsPayableGlAccountExternalId;
    return externalId ? (_store$glAccountsByEx = store.glAccountsByExternalId[keyOf(externalId)]) !== null && _store$glAccountsByEx !== void 0 ? _store$glAccountsByEx : null : null;
  },
  /** The property utility a pair is: its id, null when the property has none, undefined until asked. */
  getPropertyUtilityId(propertyId, utilityId) {
    if (!propertyId || !utilityId) return null;
    return store.propertyUtilityByPair[pairKey(propertyId, utilityId)];
  },
  /**
   * The account a payable for one of this property's utilities posts to: the utility's own when it is
   * configured with one, else the property's default. The order appfolio-service resolves them in
   * (`BillGlAccountResolver`), so the screen names the account the push will use.
   */
  getUtilityApGlAccount(propertyLivableId, propertyUtilityLivableId) {
    var _property$utilityGlAc, _store$glAccountsByEx2;
    if (!propertyLivableId) return null;
    const property = store.propertiesByLivableId[keyOf(propertyLivableId)];
    const utilityAccount = propertyUtilityLivableId ? property === null || property === void 0 || (_property$utilityGlAc = property.utilityGlAccounts) === null || _property$utilityGlAc === void 0 || (_property$utilityGlAc = _property$utilityGlAc.find(utility => keyOf(utility.propertyUtilityLivableId) === keyOf(propertyUtilityLivableId))) === null || _property$utilityGlAc === void 0 ? void 0 : _property$utilityGlAc.accountsPayableGlAccountExternalId : null;
    const externalId = utilityAccount || (property === null || property === void 0 ? void 0 : property.accountsPayableGlAccountExternalId);
    return externalId ? (_store$glAccountsByEx2 = store.glAccountsByExternalId[keyOf(externalId)]) !== null && _store$glAccountsByEx2 !== void 0 ? _store$glAccountsByEx2 : null : null;
  },
  /** An account by its AppFolio id, which is the form a recorded line names it in. */
  getGlAccountByExternalId(externalId) {
    var _store$glAccountsByEx3;
    return externalId ? (_store$glAccountsByEx3 = store.glAccountsByExternalId[keyOf(externalId)]) !== null && _store$glAccountsByEx3 !== void 0 ? _store$glAccountsByEx3 : null : null;
  },
  /** What one attempt posted to, or null until its read has landed. */
  getRecordedAccounts(pushAttemptId) {
    var _store$recordedByAtte;
    return pushAttemptId ? (_store$recordedByAtte = store.recordedByAttemptId[keyOf(pushAttemptId)]) !== null && _store$recordedByAtte !== void 0 ? _store$recordedByAtte : null : null;
  },
  get hasRead() {
    return store.hasReadGlAccounts && store.hasReadProperties;
  }
}));
export const appfolioGlAccounts = types.optional(AppfolioGlAccountsStore, {});